import { HeaderVariant, variantStyles } from "@/components/common/header/constants";
import { disabledIconButtonStyle, filledIconButtonStyle } from "@/data/styles";
import { FilterOptions, ProjectProps, RecursiveTranslations } from "@/data/types";
import clsx from "clsx";

export function getHeaderClasses(variant: HeaderVariant, isScrolled: boolean, className?: string) {
  return clsx(
    "text-[18px] font-extralight leading-[28px] fixed top-0 left-0 w-full z-[50] transition-all duration-300",
    variantStyles[variant],
    variant === "transparent" && isScrolled && "bg-[#161E2D]",
    className
  )
}

export function formatPrice(value: number): string {
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return value.toString();
}

export const formatNumber = (value: number | string) => {
  if (!value) return ""
  const num = typeof value === "string" ? Number(value) : value
  return new Intl.NumberFormat("en-US").format(num).replace(/,/g, " ")
}

export const getArrowIconButtonStyle = (enabled: boolean) =>
  enabled ? filledIconButtonStyle : disabledIconButtonStyle;

export function getCityOptions(availableCities: string[], translations: RecursiveTranslations) {
  return [
    { value: "", label: translations?.allProjects as string },
    ...availableCities.map((city) => ({ value: city, label: city })),
  ]
}

export function getPropertyTypeOptions(availablePropertyTypes: string[], translations: RecursiveTranslations) {
  return [
    { value: "", label: translations?.any as string },
    ...availablePropertyTypes.map((type) => ({
      value: type,
      label: translations?.[type] as string,
    })),
  ]
}

export function getStatusOptions(availableStatuses: string[], translations: RecursiveTranslations) {
  return [
    { value: "", label: translations?.any as string },
    ...availableStatuses.map((status) => ({
      value: status, 
      label: translations?.[status] as string,
    })),
  ]
}

export function getSortOptions(translations: RecursiveTranslations) {
  return [
    { value: "handoverSoonest", label: translations?.handoverSoonest as string },
    { value: "handoverLatest", label: translations?.handoverLatest as string },
    { value: "priceLow", label: translations?.priceLow as string },
    { value: "priceHigh", label: translations?.priceHigh as string },
    { value: "sizeSmall", label: translations?.sizeSmall as string },
    { value: "sizeLarge", label: translations?.sizeLarge as string },
    { value: "popularity", label: translations?.mostPopular as string },
    { value: "newest", label: translations?.newest as string },
    { value: "az", label: translations?.az as string },
  ]
}

export const filterProjects = (projects: ProjectProps[], filters: FilterOptions): ProjectProps[] => {
  let filtered = projects

  if (filters.city) {
    filtered = filtered.filter((p) => p.city === filters.city)
  }
  if (filters.propertyType) {
    filtered = filtered.filter((p) => p.propertyType === filters.propertyType)
  }
  if (filters.priceFrom !== undefined) {
    filtered = filtered.filter((p) => Number(p.startingPrice) >= filters.priceFrom!)
  }
  if (filters.priceTo !== undefined) {
    filtered = filtered.filter((p) => Number(p.startingPrice) <= filters.priceTo!)
  }

  return filtered
}


export function getHomeLinks(lang: string, labels: RecursiveTranslations) {
  const { home, projects, developers, aboutUs, contactUs } = labels;

  return [
    { label: home, href: `/${lang}` },
    { label: projects, href: `/${lang}/projects` },
    { label: developers, href: `/${lang}/developers` },
    { label: aboutUs, href: `/${lang}/about-us` },
    { label: contactUs, href: `/${lang}/contact-us` },
  ];
}

export function getProjectHeaderNav(lang: string, slug: string, labels: RecursiveTranslations) {
  const { overview, amenities, gallery, paymentPlan, floorPlans, location, masterPlan } = labels;

  return [
    { label: overview, href: `/${lang}/projects/${slug}/overview` },
    { label: amenities, href: `/${lang}/projects/${slug}/amenities` },
    { label: gallery, href: `/${lang}/projects/${slug}/gallery` },
    { label: paymentPlan, href: `/${lang}/projects/${slug}/payment-plan` },
    { label: floorPlans, href: `/${lang}/projects/${slug}/floor-plans` },
    { label: location, href: `/${lang}/projects/${slug}/location` },
    { label: masterPlan, href: `/${lang}/projects/${slug}/master-plan` },
  ];
}